/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { createContext, useContext } from "react";

/**
 * Adjusts the semantic level of headings rendered by CosmosText / CosmosRichText
 * within a region, without touching their visual size (which is variant-driven).
 *
 * Motivation: a page's hero owns the single <h1>. Content regions below it (e.g.
 * an article body) are authored with components that hardcode `as="h3"` etc.,
 * which — sitting directly under the hero h1 — produce an h1→h3 SKIPPED_LEVEL.
 * Wrapping such a region in `<HeadingLevelContext.Provider value={{ offset: -1 }}>`
 * shifts those component headings up one level (h3→h2, h4→h3, …), so they nest
 * correctly under the hero. The shift is floored at h2 so it can never emit a
 * second <h1>.
 *
 * Default offset is 0 — every heading outside a provider is unaffected, so this
 * is inert for the rest of the design system.
 */
export interface HeadingLevelContextValue {
	offset: number;
	/**
	 * When set, a `SectionHeading` inside this region renders its title at this level
	 * (unless the title has an explicit `as`). Used to promote the FIRST ref-section on a
	 * hero-less page to the page `<h1>` — the section acts as the missing hero. Independent
	 * of `offset` (which shifts descendant card headings and floors at h2).
	 */
	sectionTitleAs?: "h1" | "h2";
}

export const HeadingLevelContext = createContext<HeadingLevelContextValue>({ offset: 0 });

export const useHeadingLevelOffset = (): number => useContext(HeadingLevelContext).offset;

/** Level a `SectionHeading` should use for its title (from a surrounding provider), if any. */
export const useSectionTitleAs = (): "h1" | "h2" | undefined => useContext(HeadingLevelContext).sectionTitleAs;

/**
 * Offset a card region should apply to its card headings. Shifts them up one level (→ h2)
 * when either the section has no title (cards are the section's top headings) OR the section
 * title has been promoted to the page h1 (getSections' first hero-less section) — in both
 * cases cards must start at h2 to avoid an h1/h2→h3 gap. Otherwise no shift (title h2 → cards h3).
 */
export const useCardHeadingOffset = (hasSectionTitle: boolean): number =>
	useContext(HeadingLevelContext).sectionTitleAs === "h1" || !hasSectionTitle ? -1 : 0;
